import _ from 'lodash';
import Vue from 'vue';
import { getCurrentInstance } from '@vue/composition-api';
import { HITL } from '@/api';
import { Settings } from '@or-sdk/hitl';
import {
  migrateToUiLayoutV3,
  translateWorkspaceList,
  areWorkspaceListsEqual,
} from '@/modules/layout-builder/helpers';
import { ICard, LayoutWorkspaceList } from '@/modules/layout-builder/types';
import {
  useUILayoutSettingsStore,
  useUILayoutSettingsSetters,
  LocalLayoutSettings,
} from './use-ui-layout-settings-store';
import { useLocalSettings } from './use-local-settings';
import { useToastV3 } from '@onereach/ui-components-25';
import { UIEventBus, THROW_API_ERROR } from '@/common/ui-event-bus';
import { SETTINGS_CATEGORIES } from '@/constants';

interface ISaveOptions {
  oldWorkspaceList?: LayoutWorkspaceList;
  updatedWorkspaceIds?: string[];
}

export enum UISettingsUpdateToastType {
  UPDATE_AVAILABLE = 'UPDATE_AVAILABLE',
  UPDATED = 'UPDATED',
}

/* eslint-disable-next-line */
export function useUILayoutSettingsActions() {
  const store = useUILayoutSettingsStore();
  const setters = useUILayoutSettingsSetters();
  const { updateLocalSettings } = useLocalSettings();
  const { success } = useToastV3();
  const vm = getCurrentInstance()!;

  async function init(): Promise<void> {
    await getSettings();
    await setup();
    await getCards();
  }

  async function getCards(): Promise<void> {
    const cardIdList = store.layoutCardIds;

    if (!cardIdList || !cardIdList.length) {
      return;
    }

    try {
      const cards: ICard[] = await Vue.prototype.$helpers.getCards({
        ids: cardIdList,
      });
      const cardSet = _.keyBy(cards, 'id');
      setters.setCardComponentsSet(cardSet);
    } catch (error) {
      UIEventBus.$emit(THROW_API_ERROR, { error });
    }
  }

  function prepareSettings(response: Settings.GetSettingsResponse) {
    const uiLayout = response.data[SETTINGS_CATEGORIES.LAYOUT];
    let workspaces = response.data[SETTINGS_CATEGORIES.UI_WORKSPACE] ?? [];
    if (!Array.isArray(workspaces)) {
      workspaces = [workspaces];
    }
    return migrateToUiLayoutV3({
      ...(uiLayout as any)?.data,
      workspaceList: workspaces.map(w => w.data),
    });
  }

  async function getSettings(): Promise<void> {
    try {
      const CATEGORIES = [SETTINGS_CATEGORIES.LAYOUT, SETTINGS_CATEGORIES.UI_WORKSPACE];
      const responseDefault = await HITL.settings.getSettings({
        isCommon: true,
        categories: Object.values(CATEGORIES),
      });

      const settingsDefault = prepareSettings(responseDefault);
      setters.setDefaultLayoutSettings(settingsDefault);

      const responseAgent = await HITL.settings.getSettings({
        asRecord: true,
        categories: Object.values(CATEGORIES),
      });
      const settingsAgent = prepareSettings(responseAgent);
      setters.updateLayoutSettings({
        ...settingsAgent,
        header: settingsDefault.header,
        timeline: settingsDefault.timeline,
      });
    } catch (error) {
      UIEventBus.$emit(THROW_API_ERROR, { error });
    }
  }

  function getUpdatedWorkspaceList() {
    const localList = _.cloneDeep(store.layout.workspaceList);
    const defaultList = _.cloneDeep(store.defaultLayout.workspaceList!);

    return _.cloneDeep(defaultList)
      .map(workspace => {
        workspace.original = _.cloneDeep(workspace);
        const localWorkspace = localList.find(w => w.key === workspace.key);

        // save changes to the current workspace made by the agent
        if (localWorkspace) {
          workspace.isActive = localWorkspace.isActive;

          Object.values(localWorkspace.layouts).forEach(layout => {
            // if the layout is created by agent or its default state hasn't been changed
            if (layout.madeByAgent) {
              workspace.layouts[layout.key] = layout;
            } else if (workspace.layouts[layout.key]) {
              const localDefaultWorkspace = localWorkspace.original;
              const localDefaultLayout = localDefaultWorkspace?.layouts[layout.key];
              if (
                localDefaultLayout &&
                _.isEqual(localDefaultLayout, workspace.layouts[layout.key])
              ) {
                workspace.layouts[layout.key] = layout;
              }
            }
          });

          // keep the saved defaultLayout for the workspace if possible
          if (workspace.layouts[localWorkspace.defaultLayout]) {
            workspace.defaultLayout = localWorkspace.defaultLayout;
          }
        }
        return workspace;
      });
  }

  function migrateDefaultSettings() {
    const { items, version, header } = store.layout ?? {};
    if (items) {
      const settingsData = migrateToUiLayoutV3({
        header,
        version,
        items,
      } as any);
      const migratedWorkspace = settingsData.workspaceList[0];
      const workspaceList = store.layout.workspaceList.map(workspace => {
        return workspace.key === migratedWorkspace.key
          ? { ...workspace, ...migratedWorkspace }
          : workspace;
      });
      setters.updateLayoutSettings({ workspaceList });
    }
  }

  async function migrateLocalSettings() {
    const { items, version } = store.local.uiLayout ?? {};
    if (items) {
      const settingsData = migrateToUiLayoutV3({
        header: store.layout.header,
        version,
        items,
      } as any);
      const migratedWorkspace = settingsData.workspaceList[0];
      const workspaceList = store.layout.workspaceList.map(workspace => {
        return workspace.key === migratedWorkspace.key
          ? { ...workspace, ...migratedWorkspace }
          : workspace;
      });
      setters.updateLayoutSettings({ workspaceList });

      await save();
      const newLocalSettings = {
        ..._.omit(store.local.uiLayout, 'items'),
        version: settingsData.version,
      } as LocalLayoutSettings;
      updateLocalSettings({
        ...store.local,
        uiLayout: newLocalSettings,
      });
    }
  }

  async function onFirstTimeRun(defaultWorkspaceList: LayoutWorkspaceList) {
    const workspaceList = defaultWorkspaceList.map(defaultWorkspace => {
      const workspace = _.cloneDeep(defaultWorkspace);
      const original = _.cloneDeep(defaultWorkspace);
      return { ...workspace, original };
    });
    setters.updateLayoutSettings({ workspaceList });
    migrateDefaultSettings();
    await save();
  }

  async function setup() {
    const defaultWorkspaceList = store.defaultLayout.workspaceList!;
    let localDefaultWorkspaceList = [] as LayoutWorkspaceList;
    store.layout.workspaceList.forEach(workspace => {
      const { original } = workspace;
      if (original) {
        localDefaultWorkspaceList.push(original);
      }
    });

    if (_.isEmpty(localDefaultWorkspaceList)) {
      await onFirstTimeRun(defaultWorkspaceList);
      localDefaultWorkspaceList = defaultWorkspaceList;
    }

    await migrateLocalSettings();

    let updateSettingsNow = false;

    const trancelatedLocalList = translateWorkspaceList(
      localDefaultWorkspaceList,
      vm.proxy.$t.bind(vm.proxy),
    );

    const trancelatedDefaultList = translateWorkspaceList(
      defaultWorkspaceList,
      vm.proxy.$t.bind(vm.proxy),
    );

    if (
      !areWorkspaceListsEqual(trancelatedLocalList, trancelatedDefaultList)
    ) {
      updateSettingsNow = !!store.local.uiLayout?.isUpdateAvailable;

      if (updateSettingsNow) {
        await updateUiSettings();
      } else {
        const newLocalSettings = {
          ...store.local.uiLayout,
          isUpdateAvailable: !updateSettingsNow,
        } as LocalLayoutSettings;
        updateLocalSettings({
          ...store.local,
          uiLayout: newLocalSettings,
        });
      }
    } else {
      updateLocalSettings({
        ...store.local,
        uiLayout: {
          ...store.local.uiLayout,
          isUpdateAvailable: false,
        } as LocalLayoutSettings,
      });
    }

    setters.initializeLayoutSettings(updateSettingsNow);
  }

  async function save(options: ISaveOptions = {}) {
    const { oldWorkspaceList, updatedWorkspaceIds } = options;
    const CATEGORY = SETTINGS_CATEGORIES.UI_WORKSPACE;
    let updatedWorkspaceList = store.layout.workspaceList;
    if (updatedWorkspaceIds) {
      updatedWorkspaceList = updatedWorkspaceList.filter(w => updatedWorkspaceIds.includes(w.key));
    }
    const settings = updatedWorkspaceList
      .map(workspace => {
        const { key, ruleTags } = workspace;
        return {
          category: CATEGORY,
          categoryItemId: key,
          ruleTags: ruleTags,
          data: workspace,
        };
      });
    try {
      await HITL.settings.updateSettings({
        settings,
      });

      if (oldWorkspaceList) {
        const oldWorkspaceKeys = oldWorkspaceList.map(w => w.key);
        const workspaceKeys = store.layout.workspaceList.map(w => w.key);
        const deletedWorkspaceKeys = _.difference(oldWorkspaceKeys, workspaceKeys);
        for (const key of deletedWorkspaceKeys) {
          await HITL.settings.deleteSetting({
            category: CATEGORY,
            categoryItemId: key,
          });
        }
      }
    } catch (error) {
      UIEventBus.$emit(THROW_API_ERROR, { error });
    }
  }

  async function reset() {
    setters.resetLayoutSettings();
    await save();
  }

  async function updateUiSettings() {
    const { uiLayout } = store.local;
    const oldWorkspaceList = store.layout.workspaceList;
    const workspaceList = getUpdatedWorkspaceList();

    const activeWorkspace = workspaceList.find(workspace => workspace.isActive);
    if (!activeWorkspace) {
      workspaceList[0].isActive = true;
    }

    setters.updateLayoutSettings({
      ...store.layout,
      workspaceList,
    });

    await save({ oldWorkspaceList });

    const newLocalSettings = {
      ...uiLayout,
      isUpdateAvailable: false,
    } as LocalLayoutSettings;
    updateLocalSettings({
      ...store.local,
      uiLayout: newLocalSettings,
    });
  }

  function showUpdateToast(toastType: UISettingsUpdateToastType) {
    if (toastType === UISettingsUpdateToastType.UPDATED) {
      success({
        messageText: vm.proxy.$t('hitl.agentUi.layoutSwitcher.update-toast.updated') as string,
      });
    } else if (toastType === UISettingsUpdateToastType.UPDATE_AVAILABLE) {
      const { close } = success({
        messageText: vm.proxy.$t('hitl.agentUi.layoutSwitcher.update-toast.update-available') as string,
        link: {
          title: vm.proxy.$t('hitl.agentUi.layoutSwitcher.update-toast.update-now') as string,
          href: '',
          callback: async () => {
            await updateUiSettings();
            close();
            window.location.reload();
          },
        },
      }, { duration: 0 });
    }
  }

  return {
    getCards,
    initializeUILayoutSettings: init,
    saveUILayoutSettings: save,
    resetUILayoutSettings: reset,
    showSettingsUpdateToast: showUpdateToast,
  };
}
